// Crew Mode pilot shell v0 — bundle A (home / task launcher).
//
// One place that names the four P0 capture streams: their route, their
// launcher label, an icon, and the per-farm feature-flag check each one
// already ships with. `CrewModeHome` renders a launcher button per
// enabled stream from this list; `Sidebar` shows the `nav.crewMode` entry
// only when `isAnyCrewModeStreamEnabled` is true for the active farm.
//
// This adds NO new flag granularity (PRD §4 non-goal #3) — it only ORs
// the four existing `isXCrewModeEnabled(farmId)` checks.

import type { TranslationKey } from '../../i18n/en'
import { getActiveTenantKey } from '../../offline/tenant'
import type { CrewModeStreamId } from '../../utils/api'
import { isWaterLogCrewModeEnabled } from './waterLogFeatureFlag'
import { isFeedCrewModeEnabled } from './feedLogFeatureFlag'
import { isMortalityCrewModeEnabled } from './mortalityLogFeatureFlag'
import { isWeightSampleCrewModeEnabled } from './weightSampleLogFeatureFlag'

export type { CrewModeStreamId }

export type CrewModeStreamDescriptor = {
  id: CrewModeStreamId
  /** The existing URL-only capture route this stream is reached at. */
  route: string
  /** Launcher button label. */
  labelKey: TranslationKey
  /** Icon glyph shown alongside the label on the launcher button. */
  icon: string
  /** Per-farm feature-flag check this stream already ships with. */
  isEnabled: (farmId: string, tenantKey?: string) => boolean
}

export const CREW_MODE_STREAMS: readonly CrewModeStreamDescriptor[] = [
  {
    id: 'water',
    route: '/crew/water',
    labelKey: 'crewMode.home.stream.water',
    icon: '💧',
    isEnabled: isWaterLogCrewModeEnabled
  },
  {
    id: 'feed',
    route: '/crew/feed',
    labelKey: 'crewMode.home.stream.feed',
    icon: '🥣',
    isEnabled: isFeedCrewModeEnabled
  },
  {
    id: 'mortality',
    route: '/crew/mortality',
    labelKey: 'crewMode.home.stream.mortality',
    icon: '⚠️',
    isEnabled: isMortalityCrewModeEnabled
  },
  {
    id: 'weightSample',
    route: '/crew/weight-sample',
    labelKey: 'crewMode.home.stream.weightSample',
    icon: '⚖️',
    isEnabled: isWeightSampleCrewModeEnabled
  }
] as const

/**
 * The streams flag-enabled for `farmId`, in launcher order. Empty when the
 * farm is not a pilot farm for any stream.
 */
export const enabledCrewModeStreams = (
  farmId: string,
  tenantKey = getActiveTenantKey()
): CrewModeStreamDescriptor[] => {
  if (!farmId) return []
  return CREW_MODE_STREAMS.filter((stream) => stream.isEnabled(farmId, tenantKey))
}

/**
 * True when at least one Crew Mode capture stream is flag-enabled for
 * `farmId`. Gates the sidebar `nav.crewMode` entry so Crew Mode stays
 * invisible on non-pilot farms.
 */
export const isAnyCrewModeStreamEnabled = (
  farmId: string,
  tenantKey = getActiveTenantKey()
): boolean => enabledCrewModeStreams(farmId, tenantKey).length > 0
